import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './AgentLayout.css';

const AgentLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [backgroundTheme, setBackgroundTheme] = useState('none');
  
  // Force re-render on location change - FIX for browser back button
  const [, forceUpdate] = useState({});
  
  useEffect(() => {
    // Force component to re-render when location changes
    forceUpdate({});
    // Scroll to top on navigation
    window.scrollTo(0, 0);
    // Close mobile menu on navigation
    setIsMobileMenuOpen(false);
  }, [location.pathname]);
  
  // Background theme definitions
  const backgroundThemes = {
    'none': 'transparent',
    'ocean-gradient': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'sunset-gradient': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'forest-gradient': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'lavender-gradient': 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'golden-gradient': 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'aurora-gradient': 'linear-gradient(135deg, #3f2b96 0%, #a8c0ff 100%)',
    'geometric-pattern': 'url(data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E)',
    'dots-pattern': 'url(data:image/svg+xml,%3Csvg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%239C92AC" fill-opacity="0.15"%3E%3Ccircle cx="3" cy="3" r="3"/%3E%3Ccircle cx="13" cy="13" r="3"/%3E%3C/g%3E%3C/svg%3E)',
    'waves-pattern': 'url(data:image/svg+xml,%3Csvg width="100" height="20" viewBox="0 0 100 20" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M21.184 20c.357-.13.72-.264 1.088-.402l1.768-.661C33.64 15.347 39.647 14 50 14c10.271 0 15.362 1.222 24.629 4.928.955.383 1.869.74 2.75 1.072h6.225c-2.51-.73-5.139-1.691-8.233-2.928C65.888 13.278 60.562 12 50 12c-10.626 0-16.855 1.397-26.66 5.063l-1.767.662c-2.475.923-4.66 1.674-6.724 2.275h6.335zm0-20C13.258 2.892 8.077 4 0 4V2c5.744 0 9.951-.574 14.85-2h6.334zM77.38 0C85.239 2.966 90.502 4 100 4V2c-6.842 0-11.386-.542-16.396-2h-6.225zM0 14c8.44 0 13.718-1.21 22.272-4.402l1.768-.661C33.64 5.347 39.647 4 50 4c10.271 0 15.362 1.222 24.629 4.928C84.112 12.722 89.438 14 100 14v-2c-10.271 0-15.362-1.222-24.629-4.928C65.888 3.278 60.562 2 50 2 39.374 2 33.145 3.397 23.34 7.063l-1.767.662C13.223 10.84 8.163 12 0 12v2z" fill="%235C7CFA" fill-opacity="0.1" fill-rule="evenodd"/%3E%3C/svg%3E)'
  };
  
  // Load background theme from localStorage or user profile
  useEffect(() => {
    const savedTheme = localStorage.getItem('agentBackgroundTheme') || user?.backgroundTheme || 'none';
    setBackgroundTheme(savedTheme);
  }, [user]);

  // Listen for theme change events
  useEffect(() => {
    const handleThemeChange = (event) => {
      setBackgroundTheme(event.detail.theme);
    };

    window.addEventListener('backgroundThemeChanged', handleThemeChange);
    return () => {
      window.removeEventListener('backgroundThemeChanged', handleThemeChange);
    };
  }, []);
  
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/agent/profile')) return 'profile';
    if (path.includes('/agent/clients')) return 'clients';
    if (path.includes('/agent/quotes')) return 'quotes';
    if (path.includes('/agent/itineraries')) return 'itineraries';
    if (path.includes('/agent/commissions')) return 'commissions';
    if (path.includes('/agent/payment-reminders')) return 'payment-reminders';
    if (path.includes('/agent/websites')) return 'websites';
    if (path.includes('/agent/groups')) return 'groups';
    if (path.includes('/agent/analytics')) return 'analytics';
    return 'overview';
  };

  const activeTab = getActiveTab();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navigationItems = [
    { id: 'overview', label: 'Overview', path: '/agent/dashboard' },
    { id: 'clients', label: 'Clients', path: '/agent/clients' },
    { id: 'quotes', label: 'Quotes', path: '/agent/quotes' },
    { id: 'itineraries', label: 'Itineraries', path: '/agent/itineraries' },
    { id: 'commissions', label: 'Commissions', path: '/agent/commissions' },
    { id: 'analytics', label: 'Analytics', path: '/agent/analytics' },
    { id: 'payment-reminders', label: 'Payment Reminders', path: '/agent/payment-reminders' },
    { id: 'groups', label: 'Groups', path: '/agent/groups' },
    { id: 'websites', label: 'My Website', path: '/agent/websites' },
    { id: 'profile', label: 'My Profile', path: '/agent/profile' }
  ];

  return (
    <div className="agent-layout-wrapper">
      {/* Background decoration - only visible on larger screens */}
      <div 
        className="agent-background-decoration"
        style={{
          background: backgroundThemes[backgroundTheme] || 'transparent',
          backgroundSize: backgroundTheme.includes('pattern') ? '20px 20px' : 'cover'
        }}
      />
      
      <div className="agent-layout-content">
        {/* Header with calming blue gradient */}
        <header className="agent-header">
          <div className="agent-header-content">
            <div className="agent-header-inner">
              <div className="agent-header-brand">
                <h1 className="agent-header-title">
                  Travel+ CRM
                </h1>
                <span className="agent-header-subtitle">
                  Agent Dashboard
                </span>
              </div>
              
              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="agent-mobile-menu-btn"
              >
                <span className="sr-only">Open main menu</span>
                {isMobileMenuOpen ? (
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
              
              {/* Desktop user info */}
              <div className="agent-header-user">
                <span className="agent-header-welcome">
                  Welcome, {user?.name || user?.firstName ? `${user.firstName} ${user.lastName}` : user?.email || 'Agent'}
                </span>
                <button
                  onClick={() => navigate('/agent/profile')}
                  className="agent-header-profile"
                  title="My Profile"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </button>
                <button
                  onClick={handleLogout}
                  className="agent-header-logout"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
          
          {/* Mobile menu */}
          {isMobileMenuOpen && (
            <div className="agent-mobile-menu">
              <div className="space-y-1">
                {navigationItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      navigate(item.path);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`agent-mobile-menu-item ${activeTab === item.id ? 'active' : ''}`}
                  >
                    {item.label}
                  </button>
                ))}
                <div className="agent-mobile-menu-divider">
                  <div className="agent-mobile-menu-user">
                    {user?.name || user?.firstName ? `${user.firstName} ${user.lastName}` : user?.email || 'Agent'}
                  </div>
                  <button
                    onClick={handleLogout}
                    className="agent-mobile-menu-item"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          )}
        </header>

        {/* Navigation Tabs */}
        <div className="agent-nav-tabs">
          <div className="agent-nav-tabs-inner">
            <div className="agent-nav-tabs-list">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => navigate(item.path)}
                  className={`agent-nav-tab ${activeTab === item.id ? 'active' : ''}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content - Render child routes with key to force re-render */}
        <main className="agent-main-content">
          <div className="agent-main-inner">
            <Outlet key={location.pathname} />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AgentLayout;
